<template>
  <TsNuxtCard
    :data-testid="baseDataTestid"
    append-class="bg-[#DAEDFF] mb-2"
    noShadow
    noBorder
  >
    <div class="flex justify-between items-center">
      <TsTypography
        v-if="!props.hideTitle"
        variant="h2"
        append-class="mb-2 text-primary"
        :data-testid="baseDataTestid + 'order-summary-heading'"
        >{{ useTranslation("orderSummary", "Besteloverzicht") }}</TsTypography
      >
      <TsTypography
        v-if="loading"
        as="span"
        variant="sm"
        weight="semibold"
        append-class="text-idle-black mb-2"
        :data-testid="baseDataTestid + '-loading'"
        >{{ useTranslation("calculating", "Berekenen...") }}</TsTypography
      >
    </div>

    <table class="w-full text-medium text-sm font-medium">
      <tbody>
        <tr class="*:py-2.5">
          <td>
            <span :data-testid="baseDataTestid + '-subtotal-text'">
              {{ useTranslation("subtotal", "Subtotaal") }}
            </span>
            <span v-if="totalProducts" :data-testid="baseDataTestid + '-total-product-count'">
              ({{
                totalProducts > 1
                  ? `${totalProducts} ${useTranslation("products", "Producten")}`
                  : `1 ${useTranslation("product", "Product")}`
              }})
            </span>
          </td>
          <td class="text-right font-semibold" :data-testid="baseDataTestid + '-subtotal'">{{ subTotal }}</td>
        </tr>
        <tr class="*:py-2.5">
          <td :data-testid="baseDataTestid + '-total-vat-text'">{{ useTranslation("total", "Totaal") }} {{ useTranslation("vat", "btw") }}</td>
          <td class="text-right font-semibold" :data-testid="baseDataTestid + '-total-vat'">{{ totalVat }}</td>
        </tr>

        <tr v-if="trolleyStore.total_discount_amount" class="*:py-2.5">
          <td class="capitalize">{{ useTranslation("discount", "Korting") }}</td>
          <td class="text-right font-semibold" :data-testid="baseDataTestid + '-total-savings-from-promotions'">
            -€
            {{
              trolleyStore.total_discount_amount.toFixed(2).replace(".", ",")
            }}
          </td>
        </tr>
        <tr
          v-if="isFreeShip && !loading"
          class="*:pt-2.5 *:pb-5 border-b border-[#a6d4ff] font-semibold text-success"
          :data-testid="baseDataTestid + '-free-shipping-text'"
        >
          <td>{{ useTranslation("shippingCost", "Verzendkosten") }}</td>
          <td class="text-right">{{ useTranslation("free", "Vrij") }}</td>
        </tr>
        <tr
          v-else-if="shippingCost > 0 && !loading"
          class="*:pt-2.5 *:pb-5 border-b border-[#a6d4ff] font-semibold text-info"
          :data-testid="baseDataTestid + '-applied-shipping-cost'"
        >
          <td>{{ useTranslation("shippingCost", "Verzendkosten") }}</td>
          <td class="text-right">{{ `€ ${shippingCost.toFixed(2)}` }}</td>
        </tr>
        <tr class="*:pt-5 *:pb-2.5 text-[20px] font-semibold text-primary">
          <td :data-testid="baseDataTestid + '-total-inc-vat-text'">
            {{ useTranslation("total", "Total") }}
            <span class="text-xs"
              >incl. {{useTranslation("vat", "btw")}}
            </span>
          </td>
          <td class="text-right"
          :data-testid="baseDataTestid + '-total-inc-vat'"
          >{{ orderTotal }}</td>
        </tr>
        <tr class="*:py-1.5 text-[18px] text-primary">
          <td :data-testid="baseDataTestid + '-total-exc-vat-text'">{{useTranslation("total", "Totaal")}} excl. {{useTranslation("vat", "btw")}} </td>
          <td class="text-right"
          :data-testid="baseDataTestid + '-total-exc-vat'"
          >{{ totalExcVat }}</td>
        </tr>
      </tbody>
    </table>
  </TsNuxtCard>
</template>

<script lang="ts" setup>
import { useTranslation } from "~/composables/useTranslation";

type Props = {
  totalProducts?: number;
  subTotal?: string;
  totalVat?: string;
  orderTotal?: string;
  totalExcVat?: string;
  shippingCost?: number;
  isFreeShip?: boolean;
  loading?: boolean;
  hideTitle?: boolean;
  dataTestid: string;
};
const props = withDefaults(defineProps<Props>(), {
  orderTotal: "€ 00,00",
  subTotal: "€ 00,00",
  totalExcVat: "€ 00,00",
  totalVat: "€ 00,00",
  shippingCost: 0,
});

defineOptions({
  inheritAttrs: false,
});

const TsNuxtCard = defineAsyncComponent(
    () => import('~/components/organisms/TsNuxtCard.vue')
);

const baseDataTestid = computed(() => props.dataTestid + "order-summary");

const trolleyStore = useTrolleyStore();
</script>
